import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type AnnualIncome,
  type Company,
  type Inventory,
  type InventoryKind,
  type NewInventoryItem,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date, money } from '../format';
import * as Icon from '../components/Icons';
import { PitAnnualPanel } from '../components/PitAnnual';
import { HealthAnnualPanel } from '../components/HealthAnnual';

const emptyItem = (): NewInventoryItem => ({
  name: '',
  category: 'GOODS',
  quantity: '1',
  unit: 'szt.',
  unitPrice: '0.00',
});

export function InventoryPanel({ company, t, locale }: { company: Company; t: Dictionary; locale: Locale }) {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [inventories, setInventories] = useState<Inventory[] | null>(null);
  const [income, setIncome] = useState<AnnualIncome | null>(null);
  const [form, setForm] = useState<{ kind: InventoryKind; takenOn: string; items: NewInventoryItem[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, annual] = await Promise.all([
        api.inventories(company.id, year),
        api.annualIncome(company.id, year),
      ]);
      setInventories(list);
      setIncome(annual);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id, year]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const editable = !company.roles.every((role) => role === 'VIEWER' || role === 'BOARD_MEMBER');

  if (form) {
    const total = form.items.reduce(
      (sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0),
      0,
    );
    const updateItem = (index: number, change: Partial<NewInventoryItem>) =>
      setForm({
        ...form,
        items: form.items.map((current, position) => (position === index ? { ...current, ...change } : current)),
      });

    return (
      <>
        <div className="hd">
          <div>
            <h3>{t.inventory.add}</h3>
            <p>{t.inventory.lead}</p>
          </div>
        </div>
        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.addInventory(company.id, form.kind, form.takenOn, form.items);
              setForm(null);
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="iKind">{t.inventory.kind}</label>
              <select
                id="iKind"
                className="inp"
                value={form.kind}
                onChange={(event) => setForm({ ...form, kind: event.target.value as InventoryKind })}
              >
                <option value="OPENING">{t.inventory.kinds.OPENING}</option>
                <option value="CLOSING">{t.inventory.kinds.CLOSING}</option>
                <option value="INTERIM">{t.inventory.kinds.INTERIM}</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="iTaken">{t.inventory.takenOn}</label>
              <input
                id="iTaken"
                className="inp"
                type="date"
                value={form.takenOn}
                onChange={(event) => setForm({ ...form, takenOn: event.target.value })}
                required
              />
            </div>
          </div>

          <header style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 'var(--gap)' }}>
            <h4>{t.inventory.items}</h4>
            <div className="sp">
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}
              >
                <Icon.Plus />
                {t.invoices.addLine}
              </button>
            </div>
          </header>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.inventory.itemName}</th>
                  <th className="n">{t.inventory.quantity}</th>
                  <th>{t.inventory.unit}</th>
                  <th className="n">{t.inventory.unitPrice}</th>
                  <th className="n">{t.inventory.value}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {form.items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        className="inp sm"
                        value={item.name}
                        onChange={(event) => updateItem(index, { name: event.target.value })}
                        aria-label={t.inventory.itemName}
                        required
                      />
                    </td>
                    <td className="n">
                      <input
                        className="inp num sm"
                        value={item.quantity}
                        onChange={(event) => updateItem(index, { quantity: event.target.value })}
                        inputMode="decimal"
                        aria-label={t.inventory.quantity}
                        required
                      />
                    </td>
                    <td>
                      <input
                        className="inp sm"
                        style={{ maxWidth: 90 }}
                        value={item.unit}
                        onChange={(event) => updateItem(index, { unit: event.target.value })}
                        aria-label={t.inventory.unit}
                      />
                    </td>
                    <td className="n">
                      <input
                        className="inp num sm"
                        value={item.unitPrice}
                        onChange={(event) => updateItem(index, { unitPrice: event.target.value })}
                        inputMode="decimal"
                        aria-label={t.inventory.unitPrice}
                        required
                      />
                    </td>
                    <td className="n">
                      {amount(Number(item.quantity || 0) * Number(item.unitPrice || 0), locale)}
                    </td>
                    <td className="act">
                      {form.items.length > 1 && (
                        <button
                          type="button"
                          className="btn danger sm"
                          onClick={() =>
                            setForm({ ...form, items: form.items.filter((_, position) => position !== index) })
                          }
                        >
                          {t.invoices.removeLine}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>{t.invoices.totals}</td>
                  <td className="n">{amount(total, locale)}</td>
                  <td className="act" />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="bar2">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? t.companies.saving : t.inventory.save}
            </button>
            <div className="sp">
              <button type="button" className="btn ghost" onClick={() => setForm(null)} disabled={busy}>
                {t.companies.cancel}
              </button>
            </div>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.inventory.title}</h3>
          <p>{t.inventory.lead}</p>
        </div>
        <div className="sp">
          <input
            className="inp w num"
            type="number"
            aria-label={t.inventory.year}
            value={year}
            onChange={(event) => setYear(Number(event.target.value))}
          />
          {editable && (
            <button
              type="button"
              className="btn"
              onClick={() => setForm({ kind: 'CLOSING', takenOn: `${year}-12-31`, items: [emptyItem()] })}
            >
              <Icon.Plus />
              {t.inventory.add}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {/*
       * Sześć liczb rachunku rocznego jako lista, a nie akapit sklejony kropkami: kolejność
       * wierszy pokazuje sam rachunek — przychód minus wartość sprzedanych towarów,
       * w której siedzą oba remanenty.
       */}
      {/*
        Rozliczenie roczne stoi przy remanencie, bo to z niego bierze się dochód roku: towar,
        który został na półce, nie jest kosztem tego roku.
      */}
      <PitAnnualPanel company={company} year={year} t={t} locale={locale} />
      {/*
        Roczna zdrowotna stoi zaraz pod rocznym PIT-em: ta sama podstawa, inna stawka i inny termin
        (20 maja, a nie 30 kwietnia). Rozdzielenie ich znaczyłoby, że w maju ktoś szuka drugiej
        kwoty gdzie indziej — a obie wynikają z tego samego dochodu roku.
      */}
      <HealthAnnualPanel company={company} year={year} t={t} locale={locale} />

      {income && (
        <div className="panel">
          <header>
            <h4>
              {t.inventory.income} {year}
            </h4>
          </header>
          <dl className="brk" style={{ maxWidth: 560 }}>
            <dt>{t.inventory.revenue}</dt>
            <dd>{money(income.revenue, locale)}</dd>
            <dt>{t.inventory.purchases}</dt>
            <dd>{money(income.purchases, locale)}</dd>
            <dt>{t.inventory.opening}</dt>
            <dd>{money(income.openingInventory, locale)}</dd>
            <dt>{t.inventory.closing}</dt>
            <dd>{money(income.closingInventory, locale)}</dd>
            <dt>{t.inventory.costOfGoodsSold}</dt>
            <dd>{money(income.costOfGoodsSold, locale)}</dd>
            <dt className="tot">{t.inventory.income}</dt>
            <dd className="tot">{money(income.income, locale)}</dd>
          </dl>
        </div>
      )}

      <div className="panel">
        <header>
          <h4>
            {t.inventory.title} {year}
          </h4>
        </header>

        {/* Ostrzeżenia mają znaczenie podatkowe: bez remanentu dochód bywa zaniżony albo zawyżony */}
        {income?.warnings.map((warning) => (
          <div className="alert a-warn" key={warning}>
            <Icon.Warn />
            <div>
              <b>{t.inventory.warnings[warning]}</b>
            </div>
          </div>
        ))}

        {inventories?.length === 0 ? (
          <div className="empty">
            <Icon.Stock />
            <b>{t.inventory.none}</b>
            <p>{t.inventory.noneHint}</p>
            {editable && (
              <div className="row" style={{ justifyContent: 'center' }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setForm({ kind: 'CLOSING', takenOn: `${year}-12-31`, items: [emptyItem()] })}
                >
                  {t.inventory.add}
                </button>
              </div>
            )}
          </div>
        ) : (
          inventories && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.inventory.kind}</th>
                    <th>{t.inventory.takenOn}</th>
                    <th className="n">{t.inventory.value}</th>
                    <th>{t.inventory.status}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {inventories.map((inventory) => (
                    <tr key={inventory.id}>
                      <td>{t.inventory.kinds[inventory.kind]}</td>
                      <td className="doc">{date(inventory.takenOn, locale)}</td>
                      <td className="n">{amount(inventory.totalValue, locale)}</td>
                      <td>
                        <span className={inventory.status === 'DRAFT' ? 'chip st-draft' : 'chip st-ok'}>
                          <i />
                          {t.inventory.statuses[inventory.status]}
                        </span>
                      </td>
                      <td className="act">
                        <div className="row">
                          {editable && inventory.status === 'DRAFT' && (
                            <>
                              <button
                                type="button"
                                className="btn ghost sm"
                                disabled={busy}
                                onClick={() => void run(() => api.approveInventory(company.id, inventory.id))}
                              >
                                {t.inventory.approve}
                              </button>
                              <button
                                type="button"
                                className="btn danger sm"
                                disabled={busy}
                                onClick={() => void run(() => api.deleteInventory(company.id, inventory.id))}
                              >
                                {t.inventory.delete}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </>
  );
}
